import { createGlobalStyle } from 'styled-components';

// UDS Modal renders via a portal, so scoped styles can't reach it - keyed off
// [role="dialog"], mount only while the modal instance they belong to is open.

export const ModalWidthOverride = createGlobalStyle`
  [role="dialog"] [style*="max-width"] {
    max-width: ${props => props.width || '1280px'} !important;
  }
`;

export const StickyOrderModalHeader = createGlobalStyle`
  /* UDS Modal's own heading defaults to headingLevel="h3" too, but so do many
     body Typography headings (e.g. "Order Summary" section titles) when
     rendered with block prop - which wraps the text in an extra inner div
     inside the h3. The Modal's own (non-block) heading renders the h3 with a
     bare text node, no wrapping div. Exclude the block case everywhere below
     so we only ever paint the actual modal title bar, not body sub-headings. */

  /* Let the header's wrapper grow to its full content height instead of being
     flex-shrunk to the scroll container - otherwise the sticky range is capped
     and the header scrolls away with the rest of the content instead of
     sticking. A 2-level :has():not() combo here (matching the grandparent by
     shape) hits a browser :has() nesting limit and silently matches nothing,
     so this one is intentionally NOT guarded against body headings - the
     worst case is an extra flex-shrink:0 on an unrelated wrapper, which is
     harmless, versus a completely non-functional sticky header. */
  [role="dialog"] div:has(> div > h3[role="heading"]) {
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
    flex-basis: auto !important;
    min-height: auto !important;
    height: auto !important;
  }

  /* The purple heading bar itself. Values below were pulled directly from
     sof2's live computed styles (qa.mysalesorders.com order-details modal),
     not guessed: border-radius 10px on ALL corners (not just the top two -
     it's a fully-rounded, inset "floating chip", not an edge-to-edge bar),
     box-shadow 0 4px 12px rgba(0,0,0,0.25), margin-left/right -16px
     (overriding UDS's own default -32px so the bar stops 16px short of the
     card's real edge, leaving visible side margins), margin-top -16px to
     bleed past the card's own paddingTop (24px) far enough to sit flush with
     its top edge - sticky elements stick at their scroll ancestor's PADDING
     edge, not its border edge, so without this the bar sits inset below a
     plain white strip with the card's own corner. */
  [role="dialog"] div:has(> h3[role="heading"]):not(:has(> h3[role="heading"] > div)) {
    position: sticky !important;
    top: 0 !important;
    z-index: 3 !important;
    margin-top: -16px !important;
    margin-left: -16px !important;
    margin-right: -16px !important;
    margin-bottom: 16px !important;
    background-color: #4b286d !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
    border-radius: 10px !important;
    min-height: 56px !important;
    justify-content: center !important;
  }

  /* UDS's default heading colour is dark grey - force white on the purple bar */
  [role="dialog"] div:has(> h3[role="heading"]):not(:has(> h3[role="heading"] > div)) > h3[role="heading"] {
    color: #ffffff !important;
  }

  /* The header's close (X): white to match, pinned + vertically centred.
     :has(~ div h3[role="heading"]) disambiguates the MODAL HEADER close button
     from any other close button inside the dialog body. */
  [role="dialog"] div:has(> button[aria-label="Close"]):has(~ div h3[role="heading"]) svg,
  [role="dialog"] div:has(> button[aria-label="Close"]):has(~ div h3[role="heading"]) svg * {
    fill: #ffffff !important;
    color: #ffffff !important;
  }
  [role="dialog"] div:has(> button[aria-label="Close"]):has(~ div h3[role="heading"]) {
    position: sticky !important;
    top: 0 !important;
    align-self: flex-end !important;
    height: 56px !important;
    justify-content: center !important;
    margin-bottom: -56px !important;
    padding-top: 0 !important;
    z-index: 4 !important;
  }

  /* Any OTHER close (X) in the dialog - not the header's - keeps a dark icon so
     it stays visible against a light background (it renders white by default). */
  [role="dialog"] div:has(> button[aria-label="Close"]):not(:has(~ div h3[role="heading"])) svg,
  [role="dialog"] div:has(> button[aria-label="Close"]):not(:has(~ div h3[role="heading"])) svg * {
    fill: #2a2c2e !important;
    color: #2a2c2e !important;
  }

  [role="dialog"] div:has(> div > div > h3[role="heading"]) {
    padding-top: 0 !important;
  }
`;
